import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Zap,
  Calendar,
  Bell,
  CheckCircle,
  QrCode,
  Mail,
  ChevronDown,
  HelpCircle
} from 'lucide-react';
import Navigation from '../components/Navigation';

const BadgeActivationPage = () => {
  const [formData, setFormData] = useState({
    badgeId: '',
    email: '',
    terms: false
  });
  const [openFaq, setOpenFaq] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Badge activation:', formData);
  };

  const benefits = [
    {
      icon: Zap,
      title: 'Fast Entry',
      description: 'Skip lines with your activated badge'
    },
    {
      icon: Calendar,
      title: 'Personalized Schedule',
      description: 'Sync your favorite sessions automatically'
    },
    {
      icon: Bell,
      title: 'Exclusive Updates',
      description: 'Get real-time notifications during the event'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Enter your badge ID',
      description: 'Found in your confirmation email'
    },
    {
      number: '2',
      title: 'Confirm your email',
      description: 'Use the same email from purchase'
    },
    {
      number: '3',
      title: 'Get your confirmation QR',
      description: 'Save to your phone for quick access'
    }
  ];

  const faqs = [
    {
      question: 'Where do I find my badge ID?',
      answer: 'Your badge ID is located in the confirmation email you received after purchasing your ticket. Look for a code starting with "KC2025-".'
    },
    {
      question: 'Can I activate multiple badges?',
      answer: 'Yes! If you purchased multiple tickets, each will have a unique badge ID. Activate each one separately using the email associated with each ticket.'
    },
    {
      question: 'What if I lose my badge?',
      answer: 'Visit the Badge Services desk at the main entrance. Bring a photo ID and your confirmation email. We\'ll issue a replacement for $10.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3B] via-[#1a2f5a] to-white" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Badge <span className="text-[#CFA64B]">Activation</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Activate your KNOWN CON badge to unlock full event access.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
              Activate My Badge
            </Button>
            <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white px-10 py-6 text-lg">
              Buy Tickets
            </Button>
          </div>
        </div>
      </div>

      {/* Section 1: Why You Need to Activate */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Why You Need to Activate</h2>
            <p className="text-xl text-gray-600">Make the most of your KNOWN CON experience</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-8 h-8 text-[#CFA64B]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: How to Activate */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">How to Activate</h2>
            <p className="text-xl text-gray-600">Three simple steps to get started</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {steps.map((step, idx) => (
              <Card key={idx} className="border-2 border-gray-200">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B] text-white rounded-full flex items-center justify-center text-3xl font-bold">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold text-[#0B1B3B] mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white font-bold px-10 py-6 text-lg">
              Start Activation
            </Button>
          </div>
        </div>
      </div>

      {/* Section 3: Activation Form */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[700px] mx-auto px-6">
          <div className="text-center mb-12">
            <QrCode className="w-16 h-16 text-[#CFA64B] mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Activate Your Badge</h2>
            <p className="text-xl text-gray-600">Enter your details to get started</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Badge ID *</label>
                  <input
                    type="text"
                    required
                    value={formData.badgeId}
                    onChange={(e) => setFormData({...formData, badgeId: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none text-lg"
                    placeholder="KC2025-XXXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none text-lg"
                    placeholder="your@email.com"
                  />
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={formData.terms}
                    onChange={(e) => setFormData({...formData, terms: e.target.checked})}
                    className="mt-1 w-5 h-5 text-[#CFA64B] border-gray-300 rounded focus:ring-[#CFA64B]"
                  />
                  <label htmlFor="terms" className="text-gray-700">
                    I agree to the terms and conditions and privacy policy
                  </label>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Activate Badge
                </Button>
              </form>

              <div className="mt-6 text-center">
                <a href="#contact" className="text-sm text-[#CFA64B] hover:underline font-medium">
                  Need help? Contact Support
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 4: FAQs Strip */}
      <div className="py-20 bg-white">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Quick Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know about badge activation</p>
          </div>

          <div className="space-y-4 mb-8">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] transition-all">
                  <CardContent className="p-0">
                    <button
                      onClick={() => setOpenFaq(isOpen ? null : idx)}
                      className="w-full flex items-center justify-between p-6 text-left"
                    >
                      <span className="text-lg font-bold text-[#0B1B3B]">{faq.question}</span>
                      <ChevronDown className={`w-6 h-6 text-[#CFA64B] transition-transform ${
                        isOpen ? 'rotate-180' : ''
                      }`} />
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-6 pt-0">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="text-center">
            <a href="/faqs" className="inline-flex items-center text-[#CFA64B] hover:text-[#BD954A] font-bold text-lg">
              View Full FAQ Page
              <ChevronDown className="w-5 h-5 ml-2 rotate-[-90deg]" />
            </a>
          </div>
        </div>
      </div>

      {/* Section 5: CTA Footer */}
      <div className="py-20 bg-gradient-to-r from-[#CFA64B] to-[#d97706]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-8">
            Your badge is your key — activate it today and get ready for the experience.
          </h2>
          <Button className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white font-bold px-10 py-6 text-lg">
            Activate Now
          </Button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Attendee Guide</a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">FAQs</a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Contact Support</a>
          </div>
        </div>
      </div>

      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BadgeActivationPage;